import CustomRecurrenceModal from '@/components/CustomRecurrenceModal/CustomRecurrenceModal';
import { getRecurrenceOptions, weekdayOnly } from '@/constants/calendar';
import { CustomRecurrenceData } from '@/gql/events/types';
import { IconClear, IconRepeat } from '@/theme/icons';
import { parseRecurrenceFromLabel } from '@/utils/events';
import rem from '@/utils/rem';
import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  StyleProps,
  Text,
} from '@chakra-ui/react';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type RecurrenceMenuProps = {
  onOpen?: () => void;
  onClose?: () => void;
  isLoading?: boolean;
  categoryId?: string;
  recurrence: string | null;
  setSelectedRecurrence?: (recurrence: string) => void;
  setCustomRecurrence?: (recurrence: CustomRecurrenceData) => void;
  readonly?: boolean;
  width?: StyleProps['width'];
  showCategoryIcon?: boolean;
  variant?: 'default' | 'bordered';
  interval?: number;
  byDays?: { day: string }[];
  occurrences?: number;
  scheduleDate?: Date;
};

const ReccurenceMenu = ({
  onOpen,
  onClose,
  isLoading = false,
  recurrence,
  setSelectedRecurrence,
  setCustomRecurrence,
  readonly,
  width = rem(220),
  variant = 'default',
  interval = 1,
  byDays,
  occurrences,
  scheduleDate,
}: RecurrenceMenuProps) => {
  const { t } = useTranslation('RecurrenceMenu');
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenCustomRecurrenceModal, setIsOpenCustomRecurrenceModal] = useState(false);
  const [prevRecurrence, setPrevRecurrence] = useState<string>();

  const formatRecurrence = useMemo(() => {
    if (!recurrence) return null;

    const parsedRecurrence = parseRecurrenceFromLabel(recurrence);

    const formattedDays = byDays?.map((d) => d.day) || [];

    const isWeekdayOnly =
      formattedDays.length === weekdayOnly.length &&
      formattedDays.every((day) => weekdayOnly.some((wd) => wd.day === day));

    if (parsedRecurrence === 'daily' && interval > 1) {
      return `Every ${interval} days`;
    }

    if (parsedRecurrence === 'weekly' && Array.isArray(byDays) && byDays.length > 0 && !isWeekdayOnly) {
      return `Every ${interval > 1 ? `${interval} weeks` : 'week'} on ${formattedDays.join(', ')}`;
    }

    if (parsedRecurrence === 'monthly' && interval > 1) {
      return `Every ${interval} months`;
    }

    if (parsedRecurrence === 'yearly' && interval > 1) {
      return `Every ${interval} years`;
    }

    return null;
  }, [recurrence, interval, byDays]);

  const formattedOptions = useMemo(() => {
    if (scheduleDate) {
      const newOptions = getRecurrenceOptions(scheduleDate);

      const allOptions = [...newOptions];

      if (formatRecurrence) {
        allOptions.unshift({ label: formatRecurrence });
      }

      return allOptions;
    }
  }, [scheduleDate, formatRecurrence]);

  const handleSelectRecurrence = useCallback(
    (option: { label: string }) => {
      if (!option) {
        return;
      }

      if (option.label === 'Custom') {
        setIsOpenCustomRecurrenceModal(true);
        recurrence && setPrevRecurrence(formatRecurrence || recurrence);
      }

      setSelectedRecurrence && setSelectedRecurrence(option.label);
    },
    [setSelectedRecurrence],
  );

  const handleSelectCustomRecurrence = (customRecurrence: CustomRecurrenceData) => {
    setCustomRecurrence && setCustomRecurrence(customRecurrence);
    setIsOpenCustomRecurrenceModal(false);
  };

  const handleClearRecurrence = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setSelectedRecurrence && setSelectedRecurrence('');
    },
    [recurrence],
  );

  const onOpenMenu = useCallback(() => {
    setIsOpen(true);
    onOpen && onOpen();
  }, [onOpen]);

  const onCloseMenu = useCallback(() => {
    setIsOpen(false);
    onClose && onClose();
  }, [onClose]);

  const onCloseCustomRecurrenceModal = useCallback(() => {
    if (setSelectedRecurrence) {
      if (prevRecurrence) {
        setSelectedRecurrence(prevRecurrence);
      } else {
        setSelectedRecurrence('');
      }
    }
    setIsOpenCustomRecurrenceModal(false);
  }, [setIsOpenCustomRecurrenceModal, prevRecurrence]);

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" width={rem(160)}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <Menu isLazy isOpen={isOpen} onClose={onCloseMenu} onOpen={onOpenMenu}>
      <Box position="relative" width={width} lineHeight={0}>
        <MenuButton
          as={Button}
          width={width}
          height={rem(28)}
          color="text-primary"
          opacity={readonly ? 0.5 : 1}
          border={variant === 'bordered' ? `${rem(1)} solid var(--chakra-colors-stroke-primary)` : undefined}
          borderRadius={rem(4)}
          _hover={{
            backgroundColor: 'background-quaternary',
          }}
          _active={{
            backgroundColor: 'background-secondary',
          }}
          pointerEvents={readonly ? 'none' : 'visible'}
          backgroundColor="background-primary"
          paddingX={rem(8)}
        >
          <Flex
            alignItems="center"
            justifyContent="start"
            gap={2}
            minWidth={rem(24)}
            height={rem(24)}
            borderRadius="full"
          >
            <Icon as={IconRepeat} boxSize={rem(12)} />

            <Text
              textStyle="small"
              overflow="hidden"
              color="text-primary"
              textAlign="left"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {recurrence || t('labels.doesNotRepeat')}
            </Text>
          </Flex>
        </MenuButton>

        {recurrence && (
          <Button
            position="absolute"
            top="50%"
            right={rem(8)}
            minWidth={rem(16)}
            height={rem(16)}
            padding="0"
            borderRadius="99"
            _hover={{
              backgroundColor: 'background-queternary',
            }}
            transform={`translateY(-50%)`}
            aria-label="Clear Recurrence"
            backgroundColor="gray.900"
            onClick={handleClearRecurrence}
          >
            <Icon as={IconClear} boxSize={rem(8)} color="white.500" />
          </Button>
        )}
      </Box>
      <MenuList
        zIndex={3}
        overflowX="hidden"
        overflowY="auto"
        maxHeight={rem(300)}
        padding={0}
        borderRadius={4}
        boxShadow="0px 0px 10px 0px var(--chakra-colors-black-900a06)"
        backgroundColor={'gray.800'}
      >
        {formattedOptions?.map((option) => (
          <MenuItem
            key={option.label}
            as={Button}
            sx={{
              '&:hover, &:focus': {
                backgroundColor: 'background-secondary',
              },
            }}
            justifyContent="flex-start"
            fontSize="sm"
            textTransform="initial"
            borderRadius={0}
            backgroundColor="background-tertiary"
            onClick={() => handleSelectRecurrence(option)}
          >
            <Text textStyle="small" color="text-primary" noOfLines={1}>
              {option.label}
            </Text>
          </MenuItem>
        ))}
      </MenuList>
      {isOpenCustomRecurrenceModal && (
        <CustomRecurrenceModal
          currentDate={scheduleDate}
          byDays={byDays}
          occurrences={occurrences}
          interval={interval}
          recurrence={prevRecurrence}
          isOpen={isOpenCustomRecurrenceModal}
          onSave={handleSelectCustomRecurrence}
          onClose={onCloseCustomRecurrenceModal}
        />
      )}
    </Menu>
  );
};

export default ReccurenceMenu;
